// Supabase authentication provider — real accounts (email + password).
//
// Same contract as mockAuthProvider.js, plus the account features the mock cannot have:
//   login({ email, password })               → { user, token }    AuthError on failure
//   signup({ name, email, password })        → { user, token } | { pending: true, email }
//   resendConfirmation(email)                 · requestPasswordReset(email) · updatePassword(pw)
//   restore()                                 → { user } | null  (session saved by supabase-js, or
//                                                                  one created by an email link)
//   onExternalChange(fn)                      → Supabase auth events (SIGNED_OUT, PASSWORD_RECOVERY…)
//
// The role is NEVER taken from the sign-in form or from user metadata: it is read from
// public.profiles, which users cannot change (column privileges + RLS). If the profile cannot
// be read the user is treated as a learner (fail closed).
import { AuthError } from './mockAuthProvider.js';
import { ROLES } from './session.js';

export const MIN_PASSWORD = 8;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

/** Where the email links come back to: this page (no hash — the router uses the hash) + a marker. */
export const linkBack = (marker) => {
  if (typeof location === 'undefined') return undefined;
  return `${location.origin}${location.pathname}?rz=${marker}`;
};

function mapError(error, fallback = 'Something went wrong. Please try again.') {
  if (!error) return new AuthError('server', fallback);
  const code = error.code || '';
  const status = error.status || 0;
  const msg = String(error.message || '');
  if (code === 'invalid_credentials' || /invalid login credentials/i.test(msg)) return new AuthError('denied', 'Incorrect email or password.');
  if (code === 'email_not_confirmed' || /email not confirmed/i.test(msg)) return new AuthError('unconfirmed', 'Please confirm your email address first — open the link we emailed you, or send a new one below.');
  if (code === 'user_already_exists' || /already registered/i.test(msg)) return new AuthError('exists', 'An account with this email already exists. Sign in instead, or reset your password.');
  if (code === 'weak_password' || /password should/i.test(msg)) return new AuthError('weak', `Choose a stronger password (at least ${MIN_PASSWORD} characters).`);
  if (code === 'over_email_send_rate_limit' || code === 'over_request_rate_limit' || status === 429) return new AuthError('rate', 'Too many attempts. Please wait a minute and try again.');
  if (code === 'same_password') return new AuthError('weak', 'Choose a password you have not used for this account before.');
  if (error.name === 'AuthRetryableFetchError' || status === 0 || /fetch|network/i.test(msg)) return new AuthError('network', 'Cannot reach the RouteZero account service. Check your connection and try again.');
  return new AuthError('server', fallback);
}

export function createSupabaseAuthProvider({ client }) {
  const a = client.auth;

  async function profile(u) {
    try {
      const { data, error } = await client.from('profiles').select('display_name, role').eq('id', u.id).maybeSingle();
      if (!error && data) return data;
    } catch { /* fall through */ }
    return null;
  }
  async function toUser(u) {
    const p = await profile(u);
    const name = (p?.display_name || u.user_metadata?.display_name || (u.email || '').split('@')[0] || 'Learner').slice(0, 40);
    const role = p?.role === ROLES.ADMIN ? ROLES.ADMIN : ROLES.LEARNER;
    return { id: u.id, name, role, email: u.email || '' };
  }
  const checkEmail = (email) => {
    const e = String(email || '').trim();
    if (!EMAIL_RE.test(e)) throw new AuthError('email', 'Enter a valid email address.');
    return e;
  };

  return {
    name: 'supabase',
    developmentOnly: false,
    accounts: true,
    client,
    async login({ email, password } = {}) {
      const e = checkEmail(email);
      if (!password) throw new AuthError('password', 'Enter your password.');
      const { data, error } = await a.signInWithPassword({ email: e, password });
      if (error || !data?.user) throw mapError(error, 'Sign-in failed. Please try again.');
      return { user: await toUser(data.user), token: 'supabase' };
    },
    async signup({ name, email, password } = {}) {
      const nm = String(name || '').trim();
      if (!nm) throw new AuthError('invalid', 'Enter your name.');
      if (nm.length > 40) throw new AuthError('invalid', 'Names are limited to 40 characters.');
      const e = checkEmail(email);
      if (String(password || '').length < MIN_PASSWORD) throw new AuthError('weak', `Use at least ${MIN_PASSWORD} characters for your password.`);
      const { data, error } = await a.signUp({ email: e, password, options: { data: { display_name: nm }, emailRedirectTo: linkBack('confirmed') } });
      if (error) throw mapError(error, 'Could not create the account. Please try again.');
      if (data?.session && data.user) return { user: await toUser(data.user), token: 'supabase' };
      return { pending: true, email: e }; // confirmation email sent (Supabase answers the same way for an existing address)
    },
    async resendConfirmation(email) {
      const { error } = await a.resend({ type: 'signup', email: checkEmail(email), options: { emailRedirectTo: linkBack('confirmed') } });
      if (error) throw mapError(error, 'Could not send the email. Please try again.');
    },
    async requestPasswordReset(email) {
      const { error } = await a.resetPasswordForEmail(checkEmail(email), { redirectTo: linkBack('reset') });
      if (error) throw mapError(error, 'Could not send the reset email. Please try again.');
    },
    async updatePassword(password) {
      if (String(password || '').length < MIN_PASSWORD) throw new AuthError('weak', `Use at least ${MIN_PASSWORD} characters for your password.`);
      const { error } = await a.updateUser({ password });
      if (error) throw mapError(error, 'Could not change the password. Please try again.');
    },
    /** After a page load: the session supabase-js restored (or created from an email link). */
    async restore() {
      const init = await a.initialize().catch((e) => ({ error: e }));
      const { data } = await a.getSession();
      const s = data?.session;
      return { user: s?.user ? await toUser(s.user) : null, linkError: init?.error ? String(init.error.message || init.error) : null };
    },
    /** Re-read the role and name from the database (e.g. an admin role granted since). */
    async refreshUser() {
      const { data } = await a.getSession();
      return data?.session?.user ? toUser(data.session.user) : null;
    },
    async logout() { await a.signOut({ scope: 'local' }).catch(() => {}); },
    async validate() { const { data } = await a.getUser(); return !!data?.user; },
    onExternalChange(fn) { const { data } = a.onAuthStateChange((event) => fn(event)); return () => data?.subscription?.unsubscribe(); },
  };
}
